import React, { useEffect, useRef, useState } from "react";
import { BackButton, Button, CalendarIcon, Card, Chip, Input, SectionTitle, SendIcon, Textarea } from "./ui";
import { containsExternalContact } from "./store";
import api from "./api";

/* -------------------------------------------------------------------------- */
/* Chat + Appointment module                                                  */
/* -------------------------------------------------------------------------- */

export default function Chat({ chat, onSend, onUpdateAppointment, onBack }) {
  const [text, setText] = useState("");
  const [warning, setWarning] = useState("");
  const [showApt, setShowApt] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (chat?.chatId) api.chats.read(chat.chatId).catch(() => {});
  }, [chat?.chatId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [chat?.messages?.length]);

  if (!chat) {
    return (
      <div className="mx-auto max-w-5xl px-4 pb-28 pt-4 sm:px-6">
        <div className="mb-4">
          <BackButton onClick={onBack} />
        </div>
        <Card className="p-8 text-center">
          <p className="text-4xl">🐕</p>
          <p className="mt-3 font-display text-2xl font-black text-[#0B2D4D]">
            Aún no tienes conversaciones
          </p>
          <p className="mt-2 text-sm text-slate-500">
            Cuando aceptes una solicitud o reciban tu colita de vuelta, podrás
            chatear aquí.
          </p>
        </Card>
      </div>
    );
  }

  const send = async () => {
    const clean = text.trim();
    if (!clean) return;
    if (containsExternalContact(clean)) {
      setWarning(
        "Por seguridad, la comunicación debe mantenerse dentro de PawCita."
      );
      return;
    }
    try {
      await onSend(chat.dog.id, clean);
      setText("");
      setWarning("");
    } catch (err) {
      setWarning(
        err?.message ||
          "Por seguridad, la comunicación debe mantenerse dentro de PawCita."
      );
    }
  };

  return (
    <div
      className="mx-auto flex min-h-[calc(100vh-80px)] max-w-3xl flex-col px-4 pb-4 pt-4 sm:px-6"
      data-testid="chat-screen"
    >
      {/* Chat header */}
      <div className="mb-3 flex items-center justify-between">
        <BackButton onClick={onBack} testId="chat-back-btn" />
        <button
          onClick={() => setShowApt((s) => !s)}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#0B2D4D] px-3 py-1.5 text-xs font-bold text-white shadow"
          data-testid="chat-toggle-appointment"
        >
          <CalendarIcon size={14} /> Cita
        </button>
      </div>

      <Card className="flex flex-1 flex-col overflow-hidden">
        <div className="flex items-center gap-3 border-b border-slate-100 p-4">
          <img
            src={chat.dog.image || "/dog-placeholder.svg"}
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/dog-placeholder.svg"; }}
            alt={chat.dog.name}
            className="h-12 w-12 rounded-2xl object-cover"
          />
          <div className="min-w-0 flex-1">
            <p className="font-display text-lg font-black text-[#0B2D4D]">
              Chat con {chat.ownerName}
            </p>
            <p className="text-sm text-slate-500">
              {chat.dog.name} · {chat.dog.breed}
            </p>
          </div>
          <Chip tone="mint">🔒 Protegido</Chip>
        </div>

        {/* Appointment module */}
        {showApt && (
          <AppointmentModule
            appointment={chat.appointment}
            onUpdate={(apt) => onUpdateAppointment(chat.dog.id, apt)}
          />
        )}

        {/* Messages */}
        <div
          ref={scrollRef}
          className="pc-scroll min-h-[340px] flex-1 space-y-3 overflow-y-auto bg-[#FBFCFF] p-4"
          data-testid="chat-messages-container"
        >
          <div className="mx-auto w-fit rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-400">
            Hoy
          </div>
          {chat.messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.mine ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[80%] rounded-[20px] px-4 py-3 text-sm leading-relaxed shadow-sm ${
                  msg.mine
                    ? "bg-emerald-100 text-[#0B2D4D]"
                    : "border border-slate-100 bg-white text-slate-700"
                }`}
                data-testid={`chat-message-${msg.id}`}
              >
                <p>{msg.text}</p>
                <p className="mt-1 text-right text-[10px] text-slate-400">
                  {msg.time}
                </p>
              </div>
            </div>
          ))}

          {chat.appointment && (
            <InlineAppointmentCard
              appointment={chat.appointment}
              onConfirm={() =>
                onUpdateAppointment(chat.dog.id, {
                  ...chat.appointment,
                  status: "Confirmada",
                })
              }
              onCancel={() =>
                onUpdateAppointment(chat.dog.id, {
                  ...chat.appointment,
                  status: "Cancelada",
                })
              }
            />
          )}
        </div>

        {/* Input */}
        <div className="border-t border-slate-100 bg-white p-3">
          {warning && (
            <div
              className="mb-2 rounded-2xl border border-rose-100 bg-rose-50 p-3 text-xs font-bold text-rose-700"
              data-testid="chat-blocked-warning"
            >
              {warning}
            </div>
          )}
          <div className="flex items-center gap-2">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Escribe un ladridito..."
              data-testid="chat-input"
              className="min-w-0 flex-1 rounded-full border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-[#67D5B5] focus:ring-4 focus:ring-[#67D5B5]/15"
            />
            <Button
              variant="primary"
              onClick={send}
              testId="chat-send-btn"
              className="px-4"
              aria-label="Enviar ladridito"
            >
              <SendIcon size={16} />
            </Button>
          </div>
          <p className="mt-2 text-center text-[11px] text-slate-400">
            Mensajes protegidos · Se bloquean teléfonos, correos, URLs, redes
            sociales y contactos externos.
          </p>
        </div>
      </Card>
    </div>
  );
}

export function ConversationList({ chats, activityChats = [], onOpen, onOpenActivity, onBack }) {
  const bothEmpty = chats.length === 0 && activityChats.length === 0;
  return (
    <div className="mx-auto max-w-5xl px-4 pb-28 pt-4 sm:px-6" data-testid="chat-list-screen">
      <div className="mb-4">
        <BackButton onClick={onBack} testId="chat-list-back-btn" />
      </div>
      <SectionTitle eyebrow="Mensajes">Tus conversaciones</SectionTitle>
      {bothEmpty ? (
        <Card className="mt-5 p-8 text-center" data-testid="chat-list-empty">
          <p className="text-4xl">🐕</p>
          <p className="mt-3 font-display text-2xl font-black text-[#0B2D4D]">
            Aún no tienes conversaciones
          </p>
          <p className="mt-2 text-sm text-slate-500">
            Cuando aceptes una solicitud o reciban tu colita de vuelta, podrás
            chatear aquí.
          </p>
        </Card>
      ) : (
        <div className="mt-5 space-y-3">
          {chats.map((c) => {
            const last = c.messages[c.messages.length - 1];
            return (
              <button
                key={c.chatId}
                onClick={() => onOpen(c.dog.id)}
                data-testid={`conversation-${c.dog.id}`}
                className="flex w-full items-center gap-3 rounded-[22px] border border-slate-100 bg-white p-3 text-left shadow-sm transition hover:border-emerald-200 hover:shadow"
              >
                <img
                  src={c.dog.image || "/dog-placeholder.svg"}
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/dog-placeholder.svg"; }}
                  alt={c.dog.name}
                  className="h-14 w-14 flex-shrink-0 rounded-2xl object-cover"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-display text-base font-black text-[#0B2D4D]">
                    {c.ownerName} · {c.dog.name}
                  </p>
                  <p className="truncate text-sm text-slate-500">
                    {last ? last.text : "Inicia la conversación"}
                  </p>
                </div>
                <span className="text-slate-300" aria-hidden="true">›</span>
              </button>
            );
          })}

          {activityChats.length > 0 && (
            <div className="pt-2" data-testid="activity-chats-section">
              <p className="mb-2 mt-4 text-xs font-black uppercase tracking-[0.18em] text-[#67D5B5]">
                Chats de actividad
              </p>
              <div className="space-y-3">
                {activityChats.map((ac) => (
                  <button
                    key={ac.chat_id}
                    onClick={() => onOpenActivity(ac)}
                    data-testid={`activity-conversation-${ac.activity_id}`}
                    className="flex w-full items-center gap-3 rounded-[22px] border border-[#67D5B5]/40 bg-[#67D5B5]/5 p-3 text-left shadow-sm transition hover:border-[#67D5B5] hover:shadow"
                  >
                    <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-[#0B2D4D]/5 text-2xl">
                      {ac.image ? <img src={ac.image} alt={ac.activity_title} className="h-full w-full object-cover" /> : "🎉"}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-display text-base font-black text-[#0B2D4D]">
                        🎉 {ac.activity_title}
                      </p>
                      <p className="text-[11px] font-bold uppercase tracking-wide text-[#67D5B5]">Chat de actividad</p>
                      <p className="truncate text-sm text-slate-500">
                        {ac.last_message_preview || "Sin mensajes aún"}
                      </p>
                    </div>
                    <span className="flex-shrink-0 text-right text-[11px] text-slate-400">
                      {ac.last_message_at || ""}
                      <span className="block text-slate-300" aria-hidden="true">›</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InlineAppointmentCard({ appointment, onConfirm, onCancel }) {
  const statusTone =
    appointment.status === "Confirmada"
      ? "mint"
      : appointment.status === "Cancelada"
      ? "gray"
      : "yellow";

  return (
    <div
      className="mx-auto w-full max-w-md rounded-[22px] border border-emerald-100 bg-emerald-50/70 p-4 shadow-sm"
      data-testid="chat-appointment-card"
    >
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-2 font-black text-emerald-700">
          <CalendarIcon size={18} /> Cita programada
        </div>
        <Chip tone={statusTone}>{appointment.status}</Chip>
      </div>
      <div className="mt-3 space-y-1.5 text-sm font-semibold text-[#0B2D4D]">
        <p>📍 {appointment.place}</p>
        <p>
          🕙 {appointment.day}, {appointment.hour}
        </p>
        {appointment.notes && (
          <p className="text-slate-600">📝 {appointment.notes}</p>
        )}
      </div>
      {appointment.status !== "Confirmada" && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            onClick={onCancel}
            testId="appointment-cancel-btn"
          >
            Cancelar
          </Button>
          <Button
            variant="mint"
            onClick={onConfirm}
            testId="appointment-confirm-btn"
          >
            Confirmar encuentro
          </Button>
        </div>
      )}
    </div>
  );
}

function AppointmentModule({ appointment, onUpdate }) {
  const [form, setForm] = useState(
    appointment || {
      place: "Parque Omar",
      day: "Sábado",
      date: "",
      hour: "10:00",
      notes: "",
      status: "Pendiente",
    }
  );

  const save = () => {
    onUpdate({ ...form, status: form.status || "Pendiente" });
  };

  return (
    <div className="border-b border-slate-100 bg-emerald-50/50 p-4" data-testid="chat-appointment-module">
      <p className="font-display text-lg font-black text-[#0B2D4D]">
        Programar cita
      </p>
      <p className="mt-1 text-sm text-slate-500">
        Coordina un primer encuentro en un lugar público y seguro.
      </p>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Input
          label="Lugar"
          value={form.place}
          onChange={(e) => setForm({ ...form, place: e.target.value })}
          testId="appointment-place-input"
        />
        <Input
          label="Día"
          value={form.day}
          onChange={(e) => setForm({ ...form, day: e.target.value })}
          testId="appointment-day-input"
        />
        <Input
          label="Fecha"
          type="date"
          value={form.date || ""}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
          testId="appointment-date-input"
        />
        <Input
          label="Hora"
          type="time"
          value={form.hour}
          onChange={(e) => setForm({ ...form, hour: e.target.value })}
          testId="appointment-hour-input"
        />
      </div>
      <Textarea
        label="Notas"
        rows={2}
        value={form.notes}
        onChange={(e) => setForm({ ...form, notes: e.target.value })}
        testId="appointment-notes-input"
        className="mt-3"
      />
      <Button
        variant="mint"
        onClick={save}
        testId="appointment-save-btn"
        className="mt-3 w-full"
      >
        Agendar encuentro
      </Button>
    </div>
  );
}
